import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { getChecklistTasksForBE, SITE_SURVEY_ANSWER_TYPE } from '../../utils/SiteSurveyUtils';
import { CustomCellProps } from './ImportGridTypes';
import InlineEdit from '../ow_inline_todo_list/InlineEdit';

interface ChecklistItem {
	orderingId: number;
	title: string;
}

const renumber = (items: ChecklistItem[]): ChecklistItem[] =>
	items.map((item, orderingId) => ({ orderingId, title: item.title }));

const normalizeItems = (value: any): ChecklistItem[] => {
	if (Array.isArray(value)) {
		return renumber(
			value
				.filter((item) => item && typeof item.title === 'string')
				.map((item) => ({
					orderingId: typeof item.orderingId === 'number' ? item.orderingId : 0,
					title: item.title,
				}))
		);
	}
	if (typeof value === 'string' && value.trim().length > 0) {
		return getChecklistTasksForBE(value);
	}
	return [];
};

const hasNonEmptyTitle = (item: ChecklistItem): boolean =>
	typeof item.title === 'string' && item.title.trim().length > 0;

interface ChecklistRowProps {
	item: ChecklistItem;
	index: number;
	editing: boolean;
	onSetTitle: (index: number, title: string) => void;
	onEnterEdit: (index: number) => void;
	onDelete: (index: number) => void;
	onEnterKey: () => void;
}

const ChecklistRow: FC<ChecklistRowProps> = ({
	item,
	index,
	editing,
	onSetTitle,
	onEnterEdit,
	onDelete,
	onEnterKey,
}) => {
	const handleSetTitle = useCallback(
		(title: string) => onSetTitle(index, title),
		[index, onSetTitle]
	);
	const handleEnterEdit = useCallback(() => onEnterEdit(index), [index, onEnterEdit]);
	const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);

	return (
		<div className="group mb-2 flex flex-row items-center justify-between">
			<div className="mr-2 flex-1">
				{editing ? (
					<InlineEdit
						placeholder="Enter task title"
						value={item.title}
						setValue={handleSetTitle}
						onEnter={onEnterKey}
					/>
				) : (
					<div
						className="cursor-pointer rounded px-2 py-1 hover:bg-gray-50"
						onClick={handleEnterEdit}
					>
						{item.title}
					</div>
				)}
			</div>
			<DeleteOutlined
				translate=""
				onClick={handleDelete}
				className="ml-2 hidden cursor-pointer text-character-secondary group-hover:inline-block"
			/>
		</div>
	);
};

const ChecklistTasksField: FC<CustomCellProps> = ({ value, row, onChange }) => {
	const enabled = row.questionType === SITE_SURVEY_ANSWER_TYPE.CHECKLIST;
	const items = useMemo(() => normalizeItems(value), [value]);
	const [modalOpen, setModalOpen] = useState(false);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	// Buffer the items while the modal is open. Editing entirely against local
	// state — instead of round-tripping every keystroke through the parent's
	// validation + setState — keeps focus and the modal itself stable while
	// the user is mid-flow. The parent is reconciled on close.
	const [modalItems, setModalItems] = useState<ChecklistItem[]>([]);
	// Synchronously mirror modalItems so the blur → enter sequence (which fires
	// handleSetTitle and handleAddTask back-to-back in one event tick) reads
	// fresh values instead of a stale closure.
	const modalItemsRef = useRef<ChecklistItem[]>([]);
	const writeModalItems = useCallback((next: ChecklistItem[]) => {
		modalItemsRef.current = next;
		setModalItems(next);
	}, []);

	const nonEmptyItems = useMemo(() => items.filter(hasNonEmptyTitle), [items]);

	const summary = useMemo(
		() => nonEmptyItems.map((item) => item.title).join('; '),
		[nonEmptyItems]
	);

	const handleOpenModal = useCallback(() => {
		const seed = items.length === 0 ? renumber([{ orderingId: 0, title: '' }]) : renumber(items);
		writeModalItems(seed);
		setEditingIndex(items.length === 0 ? 0 : null);
		setModalOpen(true);
	}, [items, writeModalItems]);

	const handleCloseModal = useCallback(() => {
		const cleaned = renumber(modalItemsRef.current.filter(hasNonEmptyTitle));
		onChange(cleaned);
		setModalOpen(false);
		setEditingIndex(null);
	}, [onChange]);

	const handleSetTitle = useCallback(
		(index: number, title: string) => {
			const trimmed = title.trim();
			const current = modalItemsRef.current;
			const next =
				trimmed.length === 0
					? renumber(current.filter((_, idx) => idx !== index))
					: renumber(
							current.map((existingItem, idx) =>
								idx === index ? { ...existingItem, title: trimmed } : existingItem
							)
					  );
			writeModalItems(next);
			setEditingIndex(null);
		},
		[writeModalItems]
	);

	const handleAddTask = useCallback(() => {
		const current = modalItemsRef.current;
		const next = renumber([...current, { orderingId: current.length, title: '' }]);
		writeModalItems(next);
		setEditingIndex(next.length - 1);
	}, [writeModalItems]);

	const handleDelete = useCallback(
		(index: number) => {
			const current = modalItemsRef.current;
			const next = renumber(current.filter((_, idx) => idx !== index));
			writeModalItems(next);
			setEditingIndex(null);
		},
		[writeModalItems]
	);

	const handleEnterEdit = useCallback((index: number) => {
		setEditingIndex(index);
	}, []);

	const triggerIcon =
		nonEmptyItems.length === 0 ? <PlusOutlined translate="" /> : <EditOutlined translate="" />;

	const modalNonEmptyCount = modalItems.filter(hasNonEmptyTitle).length;

	return (
		<div className="flex h-full w-full items-center gap-2 overflow-hidden">
			<span
				className={`line-clamp-3 flex-1 whitespace-normal break-words ${
					summary ? 'text-character-primary' : 'text-character-secondary'
				}`}
				title={enabled ? summary : ''}
			>
				{enabled ? summary || 'No items' : ''}
			</span>
			<Button
				size="small"
				type="link"
				icon={triggerIcon}
				onClick={handleOpenModal}
				disabled={!enabled}
				aria-label={nonEmptyItems.length === 0 ? 'Add checklist items' : 'Edit checklist items'}
			/>
			<Modal
				visible={modalOpen}
				title={`Checklist items (${modalNonEmptyCount})`}
				onCancel={handleCloseModal}
				footer={
					<Button type="primary" onClick={handleCloseModal}>
						Done
					</Button>
				}
				destroyOnClose
			>
				<div className="flex flex-col">
					{modalItems.map((item, index) => (
						<ChecklistRow
							key={item.orderingId}
							item={item}
							index={index}
							editing={editingIndex === index}
							onSetTitle={handleSetTitle}
							onEnterEdit={handleEnterEdit}
							onDelete={handleDelete}
							onEnterKey={handleAddTask}
						/>
					))}
					<Button
						type="link"
						icon={<PlusOutlined translate="" />}
						onClick={handleAddTask}
						className="mt-2 self-start pl-0"
					>
						Add task
					</Button>
				</div>
			</Modal>
		</div>
	);
};

export const renderChecklistTasksCell = (props: CustomCellProps): React.ReactNode => (
	<ChecklistTasksField {...props} />
);

export default ChecklistTasksField;
